import { useCampaignTFunction } from '@retail/campaign/i18n';
import {
  CampaignTenantType,
  useDeleteCampaignItem,
  useDeleteCampaignItems,
} from '@retail/campaign/data-access';
import { useToast } from '@retail/hooks';
import { useCallback, useMemo } from 'react';
import { useInvalidateCampaignQueries } from './useInvalidateCampaignQueries';

interface Props {
  campaignId: string;
  type: CampaignTenantType;
}

export const useDeleteCampaignItemsActions = ({ campaignId, type }: Props) => {
  const toast = useToast();
  const t = useCampaignTFunction();

  const { mutateAsync: mutateDeleteItemAsync, isPending: isLoadingDeleteItem } =
    useDeleteCampaignItem({ campaignId, type });

  const { mutateAsync: mutateDeleteItemsAsync, isPending: isLoadingDeleteItems } =
    useDeleteCampaignItems({ campaignId, type });

  const invalidateCampaignQueries = useInvalidateCampaignQueries({ campaignId });

  const handleDeleteItem = useCallback(
    async (itemId: string) => {
      try {
        await mutateDeleteItemAsync({ urlVariables: itemId });
        toast.success(t('campaign.messages.itemDeleted'));
        await invalidateCampaignQueries();
      } catch (error) {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      }
    },
    [invalidateCampaignQueries, mutateDeleteItemAsync, t, toast]
  );

  const handleDeleteItems = useCallback(
    async (itemIds: string[]) => {
      try {
        await mutateDeleteItemsAsync({ body: itemIds });
        toast.success(t('campaign.messages.itemsDeleted'));
        await invalidateCampaignQueries();
      } catch (error) {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      }
    },
    [invalidateCampaignQueries, mutateDeleteItemsAsync, t, toast]
  );

  return useMemo(
    () => ({
      isLoadingDelete: isLoadingDeleteItem || isLoadingDeleteItems,
      handleDeleteItem,
      handleDeleteItems,
    }),
    [isLoadingDeleteItem, isLoadingDeleteItems, handleDeleteItem, handleDeleteItems]
  );
};
